import React, { useEffect, useState } from 'react';
import { useField } from 'informed';

import { Picker } from '@nw/omega-design-system';
import { TrashBinIcon } from '@nw/shared/ui';

import {
  AddMoreSelectFieldButton,
  MultipleSelectFieldContainer,
  MultipleSelectFieldItem,
  MultipleSelectFieldItemContainer,
  MultipleSelectFieldLabel,
  MultipleSelectFieldWrapper,
  SelectFieldDeleteIcon
} from './styledComponent';

interface MultipleSelectFieldProps {
  field: string;
  label: string;
  error: string;
  touched: boolean;
  options: Array<any>;
  formattedOptions: Array<any>;
  placeholder: string;
  initialSelectFieldNumber: number;
  minimumSelectFieldAllowed: number;
  values: any;
  validateOnBlur?: boolean;
  validationSchemaObject: any;
  isDisabled: boolean;
  reRenderObjects?: Array<any>;
  dependentOnFields: string[];
  addSelectFieldText: string;
}

export const MultipleSelectField = (
  props: MultipleSelectFieldProps
): React.ReactElement => {
  const {
    field,
    label,
    error,
    touched,
    minimumSelectFieldAllowed,
    initialSelectFieldNumber,
    options,
    placeholder,
    values,
    validateOnBlur = true,
    validationSchemaObject,
    isDisabled,
    addSelectFieldText,
    dependentOnFields,
    formattedOptions
  } = props;

  const { fieldApi, render } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: true,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    isDisabled,
    initialValue: []
  });

  const { setValue, setTouched } = fieldApi;

  const [currentSelectFields, setCurrentSelectField] = useState<
    Array<{
      key: string;
      value: string;
    }>
  >([]);

  const [currentAvailableOptions, setCurrentAvailableOptions] =
    useState(formattedOptions);

  useEffect(() => {
    if (values[field]) {
      setCurrentSelectField((selectFields) => {
        return values[field].map((value, index) => {
          return {
            key: value,
            value
          };
        });
      });
    } else {
      Array.from({ length: initialSelectFieldNumber }).map((_, index) => {
        setCurrentSelectField((selectFields) => {
          return [
            ...selectFields,
            {
              key: '',
              value: ''
            }
          ];
        });
      });
    }
  }, [...dependentOnFields?.map((field) => values[field])]);

  useEffect(() => {
    const selectFieldValues = currentSelectFields
      .filter((field) => field.value)
      .map((field) => field.value);

    const selectedOptions = options
      .filter((option) => selectFieldValues.includes(option.label))
      .map((option) => option.value);
    if (selectedOptions.length > 0) setValue(selectedOptions);
  }, [currentSelectFields]);

  const getListItemKey = (item: { label: string; value: string }) => item.value;
  const getListItemValue = (item: { label: string; value: string }) =>
    item.value;

  const onSelectionChange = (value, index) => {
    setCurrentSelectField((selectField) => {
      const newSelectField = selectField;
      newSelectField[index]['value'] = value;
      newSelectField[index]['key'] = value;
      return [...newSelectField];
    });
    setCurrentAvailableOptions((availableOption) => {
      return availableOption.filter((option) => option.value !== value);
    });
    setTouched(true);
  };

  const onClickDeleteSelectField = (index: number) => {
    const deletedOptionValue = currentSelectFields[index]['value'];
    if (minimumSelectFieldAllowed >= currentSelectFields.length) {
      return;
    }
    deletedOptionValue &&
      setCurrentAvailableOptions((availableOptions) => {
        return [
          ...formattedOptions.filter((value) =>
            [
              {
                label: deletedOptionValue,
                value: deletedOptionValue
              },
              ...availableOptions
            ].some((option) => {
              return option.value === value.value;
            })
          )
        ];
      });
    setCurrentSelectField((selectFields) => {
      const newSelectFields = selectFields;
      newSelectFields?.splice(index, 1);
      return [...newSelectFields];
    });
  };

  const onClickAddMoreSelectField = () => {
    if (values[field] && currentSelectFields.length < formattedOptions.length) {
      setCurrentSelectField((selectFields) => {
        return [
          ...selectFields,
          {
            key: '',
            value: ''
          }
        ];
      });
    }
  };

  const renderSelectField = (selectedKey, options, index) => {
    return (
      <Picker
        label={''}
        options={options}
        placeholder={placeholder}
        onSelectionChange={(value) => {
          onSelectionChange(value, index);
        }}
        error={selectedKey ? '' : error}
        selectedKey={selectedKey}
        getListItemKey={getListItemKey}
        getListItemValue={getListItemValue}
      />
    );
  };

  const handleMultipleSelectField = () => {
    return (
      <MultipleSelectFieldWrapper>
        {currentSelectFields.map((selectField, index) => {
          const { key, value } = selectField;
          const options = value
            ? [{ label: value, value }, ...currentAvailableOptions]
            : [...currentAvailableOptions];
          return (
            <MultipleSelectFieldItemContainer key={index}>
              <MultipleSelectFieldItem>
                {renderSelectField(key, options, index)}
              </MultipleSelectFieldItem>
              <SelectFieldDeleteIcon
                onClick={() => onClickDeleteSelectField(index)}
              >
                {<TrashBinIcon />}
              </SelectFieldDeleteIcon>
            </MultipleSelectFieldItemContainer>
          );
        })}
      </MultipleSelectFieldWrapper>
    );
  };

  return render(
    <MultipleSelectFieldContainer>
      <MultipleSelectFieldLabel>{label}</MultipleSelectFieldLabel>
      {handleMultipleSelectField()}
      <AddMoreSelectFieldButton onClick={onClickAddMoreSelectField}>
        {addSelectFieldText}
      </AddMoreSelectFieldButton>
    </MultipleSelectFieldContainer>
  );
};
